import { AddIcon, ArrowForwardIcon } from "@chakra-ui/icons";
import {
  Box,
  Button,
  Flex,
  LightMode,
  Spinner,
  StackProps,
  useDisclosure,
  VStack,
} from "@chakra-ui/react";
import { AccountAccessDetails } from "@syla/shared/types/responses/GetAccountsResponse";
import React, { useCallback, useMemo, useState } from "react";
import { NavLink, useHistory, useLocation } from "react-router-dom";
import { getPageUrl } from "../../../helper/getFullPageUrl";
import { buildAccountsLink } from "../../../routers/routePaths";
import { AccountStaticOptionContent } from "./AccountStaticOptionContent";
import { AddAccountCta } from "./AddAccountCta";
import { CreateAccountModal } from "./CreateAccountModal";
import { SelectAccount } from "./SelectAccount";

export function AccountSwitcher({
  accounts,
  currentAccountId,
  ...rest
}: {
  accounts: Record<string, AccountAccessDetails> | undefined;
  currentAccountId: string;
} & StackProps) {
  const currentAccount = accounts?.[currentAccountId];

  const otherAccounts = useMemo(() => {
    if (!accounts) {
      return [];
    }

    return Object.values(accounts).filter(
      (account) => account._id !== currentAccountId,
    );
  }, [accounts, currentAccountId]);

  const [searchValue, setSearchValue] = useState("");

  const createAccountDisclosure = useDisclosure();

  const location = useLocation();
  const history = useHistory();

  const getAccountUrl = useCallback(
    (switchAccountId: string) => {
      const currentLocation = getPageUrl(location, {
        path: true,
        search: true,
        hash: true,
      });

      return currentLocation.replace(currentAccountId, switchAccountId);
    },
    [currentAccountId, location],
  );

  const handleSelectAccount = useCallback(
    (account: AccountAccessDetails) => {
      history.push(getAccountUrl(account._id));
    },
    [getAccountUrl, history],
  );

  if (!currentAccount) {
    return <Spinner />;
  }

  return (
    <LightMode>
      <VStack
        w="full"
        backgroundColor="white"
        spacing={0}
        flex={1}
        minH={0}
        {...rest}
      >
        <Box
          px={2}
          py={2}
          w="full"
          borderBottomWidth="1px"
          borderColor="gray.300"
          backgroundColor="gray.50"
          alignItems="flex-start"
        >
          <AccountStaticOptionContent account={currentAccount} />
        </Box>

        {otherAccounts.length === 0 ?
          <AddAccountCta />
        : <SelectAccount
            accounts={otherAccounts}
            searchValue={searchValue}
            onSearchTermChange={setSearchValue}
            onSelect={handleSelectAccount}
          />
        }

        <Flex
          wrap="wrap"
          gap={2}
          w="full"
          alignItems="flex-start"
          backgroundColor="gray.50"
          borderTop="1px"
          borderColor="gray.300"
          px={2}
          py={1}
        >
          <Button
            size="sm"
            variant="ghost"
            colorScheme="primary"
            onClick={createAccountDisclosure.onOpen}
            leftIcon={<AddIcon />}
          >
            add new account
          </Button>

          <Button
            size="sm"
            variant="ghost"
            as={NavLink}
            to={buildAccountsLink({ accountId: currentAccountId })}
            rightIcon={<ArrowForwardIcon />}
          >
            View all accounts
          </Button>

          {createAccountDisclosure.isOpen && (
            <CreateAccountModal disclosure={createAccountDisclosure} />
          )}
        </Flex>
      </VStack>
    </LightMode>
  );
}
